import {
  normalizeSurveyQuestionLabels,
  QuestionChoiceLabels,
  SurveyChoiceLabelMap,
} from './survey-choice-labels'

/**
 * Renders stored survey answers as people read them: the twin-chat Survey
 * Answers panels and the twin's own study evidence (`labelledStudyAnswers`)
 * both read this one renderer, so the twin is told what the panel shows.
 *
 * Choice values are slugs. When a label map is present, the authored choice
 * text is shown. Raw slug keys with no label are humanized (`_` → space).
 * Authored question titles and open text are left as written. Choice lookup
 * is skipped only when the label is marked `isOpenText`. An empty value map
 * is also how identity choices are stored, so emptiness is not that marker.
 * SurveyJS "Other" text is that marker on the nested child, not inferred
 * from an `other` key on the answer.
 *
 * Pure function. Model-facing through `labelledStudyAnswers` (the twin's study
 * evidence, rooms) as well as display-facing.
 */

export interface SurveyAnswerPair {
  question: string
  answer: string
  groups: Array<string> | null
}

/** Slug identities minted for choice values and question names. */
const SLUG_KEY = /^[a-z0-9_]+$/

/** Underscores to spaces. Only for raw keys that have no authored label. */
export function humanizeRawKey(text: string): string {
  return text.replace(/_/g, ' ').replace(/ +/g, ' ').trim()
}

function answerKey(value: unknown): string {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function displayToken(value: string, labels?: Record<string, string>): string {
  if (!value) return '—'
  if (labels && Object.prototype.hasOwnProperty.call(labels, value)) {
    return labels[value]
  }
  if (SLUG_KEY.test(value)) return humanizeRawKey(value) || '—'
  return value
}

/** Explicit open-text marker. Empty `values` is not sufficient. */
function isOpenTextLabels(labels: QuestionChoiceLabels): boolean {
  return labels.isOpenText === true
}

function renderStoredText(value: string, labels?: QuestionChoiceLabels): string {
  if (!value) return '—'
  if (labels && isOpenTextLabels(labels)) return value
  return displayToken(value, labels?.values)
}

function nestedKeyLabel(key: string, nested: QuestionChoiceLabels, parent?: QuestionChoiceLabels): string {
  if (nested.title && nested.title !== key) return nested.title
  return displayToken(key, parent?.values)
}

function displayQuestion(key: string, questions: Record<string, string>): string {
  const mapped = questions[key]
  if (typeof mapped === 'string' && mapped.trim() && mapped !== key) return mapped
  return displayToken(key)
}

/**
 * A grid answer's rows in questionnaire order (decision 18): the declared rows
 * (the key order of `labels.values`, built from the definition), then any
 * undeclared key, sorted. A twin answered in its own shuffled order, which the
 * seed recomputes, so the stored key order is not the display order. A record
 * whose keys are not declared rows (a row's measures) keeps its own order.
 */
function declaredEntries(
  record: Record<string, unknown>,
  labels?: QuestionChoiceLabels,
): Array<[string, unknown]> {
  const declared = Object.keys(labels?.values ?? {}).filter((key) =>
    Object.prototype.hasOwnProperty.call(record, key),
  )
  if (declared.length === 0) return Object.entries(record)
  const undeclared = Object.keys(record)
    .filter((key) => !declared.includes(key))
    .sort()
  return [...declared, ...undeclared].map((key) => [key, record[key]])
}

function formatRecord(record: Record<string, unknown>, labels?: QuestionChoiceLabels): string {
  const parts: Array<string> = []
  for (const [key, entry] of declaredEntries(record, labels)) {
    if (isBlank(entry)) continue
    const nested = labels?.nested?.[key]
    if (nested) {
      const rendered = formatAnswerValue(entry, nested)
      if (rendered && rendered !== '—') {
        parts.push(`${nestedKeyLabel(key, nested, labels)}: ${rendered}`)
      }
      continue
    }
    if (isPlainObject(entry)) {
      const rowLabel = displayToken(key, labels?.values)
      const inner = formatRecord(entry, {
        values: {},
        nested: labels?.nested,
      })
      if (inner) parts.push(`${rowLabel}: ${inner}`)
      continue
    }
    if (labels?.columns && Object.keys(labels.columns).length > 0) {
      parts.push(
        `${displayToken(key, labels.values)}: ${displayToken(answerKey(entry), labels.columns)}`,
      )
      continue
    }
    const rendered = formatAnswerValue(entry, labels)
    if (rendered && rendered !== '—') {
      parts.push(`${displayToken(key, labels?.values)}: ${rendered}`)
    }
  }
  return parts.join('; ')
}

function formatAnswerValue(value: unknown, labels?: QuestionChoiceLabels): string {
  if (isBlank(value)) return '—'
  if (typeof value === 'number' || typeof value === 'boolean') {
    if (labels && isOpenTextLabels(labels)) return String(value)
    return displayToken(String(value), labels?.values)
  }
  if (typeof value === 'string') return renderStoredText(value, labels)
  if (Array.isArray(value)) {
    const present = value.filter((item) => !isBlank(item))
    if (present.length > 0 && present.every((item) => isPlainObject(item))) {
      const parts = present
        .map((item) => formatRecord(item as Record<string, unknown>, labels))
        .filter((part) => part && part !== '—')
      return parts.join('; ') || '—'
    }
    const parts = present
      .map((item) => formatAnswerValue(item, labels))
      .filter((part) => part && part !== '—')
    return parts.join(', ') || '—'
  }
  if (isPlainObject(value)) return formatRecord(value, labels) || '—'
  return displayToken(answerKey(value), labels?.values)
}

/**
 * Grouped by the stored answer's typed shape only: a grid of rows whose cells
 * are records (a matrix with nested columns), at least two rows. A string is
 * never grouped, whatever punctuation its text holds: open text stays verbatim.
 */
function isGroupedAnswer(value: unknown): value is Record<string, unknown> {
  if (!isPlainObject(value)) return false
  const entries = Object.values(value)
  return entries.length >= 2 && entries.some((entry) => isPlainObject(entry))
}

function formatGroupedValue(value: Record<string, unknown>, labels?: QuestionChoiceLabels): Array<string> {
  return declaredEntries(value, labels)
    .filter(([, entry]) => !isBlank(entry))
    .map(([key, entry]) => formatRecord({ [key]: entry }, labels))
    .filter((group) => group && group !== '—')
}

export function resolveSurveyAnswerPairs(
  answers: Record<string, unknown> | null | undefined,
  questions?: Record<string, string> | null,
  choiceLabels?: SurveyChoiceLabelMap | null,
): Array<SurveyAnswerPair> {
  if (!answers) return []
  const questionMap = questions ?? {}
  const pairs: Array<SurveyAnswerPair> = []
  for (const [key, value] of Object.entries(answers)) {
    if (value === null || value === undefined || value === '') continue
    if (Array.isArray(value) && value.length === 0) continue
    const labels = normalizeSurveyQuestionLabels(choiceLabels?.[key])
    const answer = formatAnswerValue(value, labels)
    const groups = isGroupedAnswer(value) ? formatGroupedValue(value, labels) : null
    if ((!groups || groups.length === 0) && (!answer || answer === '—')) continue
    pairs.push({
      question: displayQuestion(key, questionMap),
      answer,
      groups,
    })
  }
  return pairs
}

/**
 * A respondent's study answers as the respondent read them, for the twin's own
 * study evidence: per question, a scalar answer's label (text, number or
 * yes/no as shown), a multi-select's list of selected labels, and a grid's or
 * record's rendered rows. The same rendering as the Survey Answers panel
 * (`resolveSurveyAnswerPairs`); blank answers are left out. Each string is one
 * whole answer the twin gave, so a quote equal to one of them is that answer.
 */
export function labelledStudyAnswers(
  answers: Record<string, unknown> | null | undefined,
  choiceLabels?: SurveyChoiceLabelMap | null,
): Record<string, string | Array<string>> {
  const labelled: Record<string, string | Array<string>> = {}
  for (const [key, value] of Object.entries(answers ?? {})) {
    const labels = normalizeSurveyQuestionLabels(choiceLabels?.[key])
    if (Array.isArray(value) && value.every((item) => !isPlainObject(item))) {
      const items = value
        .filter((item) => !isBlank(item))
        .map((item) => formatAnswerValue(item, labels))
        .filter((item) => item && item !== '—')
      if (items.length > 0) labelled[key] = items
      continue
    }
    if (isBlank(value)) continue
    if (isGroupedAnswer(value)) {
      const groups = formatGroupedValue(value, labels)
      if (groups.length > 0) {
        labelled[key] = groups
        continue
      }
    }
    const answer = formatAnswerValue(value, labels)
    if (answer && answer !== '—') labelled[key] = answer
  }
  return labelled
}
